import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, AlertTriangle, Sparkles, CalendarDays, ReceiptText, Mail, PlayCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

function todayMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(m, delta) {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// -----------------------------------------------------------------------------
// Statutory Deadlines panel
// -----------------------------------------------------------------------------
function StatutoryPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/compliance/statutory-calendar", { params: { within_days: 180 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleReminder = async (checked) => {
    try {
      await api.post("/compliance/reminder-config", { enabled: checked });
      toast.success(`Email reminders ${checked ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error("Failed to update reminder setting"); }
  };

  const runNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/compliance/reminders/run-now", {});
      toast.success(`Scan complete · ${r.data.sent || 0} reminder(s) emailed · ${r.data.scanned || 0} scanned`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reminder scan failed"); }
    finally { setSending(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    (data?.deadlines || []).forEach((d) => {
      const mo = d.due.slice(0, 7);
      (g[mo] = g[mo] || []).push(d);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const catColor = (c) => ({
    "MCA": "bg-purple-100 text-purple-700 border-purple-200",
    "LLP": "bg-indigo-100 text-indigo-700 border-indigo-200",
    "Income Tax": "bg-blue-100 text-blue-700 border-blue-200",
    "TDS": "bg-amber-100 text-amber-700 border-amber-200",
    "Adv Tax": "bg-emerald-100 text-emerald-700 border-emerald-200",
  }[c] || "bg-muted");

  return (
    <div className="space-y-4">
      {/* Reminder toggle */}
      <Card className="p-4 rounded-sm border-2 border-accent/40 bg-accent/5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Auto reminders</div>
            <div className="font-semibold">Email admins at T-7 and T-1 before each deadline</div>
            <div className="text-xs text-muted-foreground mt-0.5">Uses Resend key configured in Settings → Compliance. Only Business Owner / Accountant users receive the email.</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!data?.reminder_enabled} onCheckedChange={toggleReminder} data-testid="compliance-reminder-switch" />
          <span className="text-sm">{data?.reminder_enabled ? "Enabled" : "Disabled"}</span>
          <Button onClick={runNow} disabled={sending || !data?.reminder_enabled} variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="compliance-reminder-run-now">
            <PlayCircle className="w-4 h-4" /> {sending ? "Sending…" : "Test send now"}
          </Button>
        </div>
      </Card>

      {/* Summary tiles */}
      {data?.counts && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3 rounded-sm">
            <div className="text-[10px] uppercase font-mono text-muted-foreground">Upcoming (180d)</div>
            <div className="text-2xl font-bold" data-testid="stat-total">{data.counts.total}</div>
          </Card>
          <Card className={`p-3 rounded-sm ${data.counts.next_30d > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
            <div className="text-[10px] uppercase font-mono text-amber-700">Within 30 days</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="stat-30d">{data.counts.next_30d}</div>
          </Card>
          <Card className={`p-3 rounded-sm ${data.counts.next_7d > 0 ? "border-red-300 bg-red-50" : ""}`}>
            <div className="text-[10px] uppercase font-mono text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Within 7 days</div>
            <div className="text-2xl font-bold text-red-700" data-testid="stat-7d">{data.counts.next_7d}</div>
          </Card>
        </div>
      )}

      {/* Deadlines grouped by month */}
      {busy && <div className="text-muted-foreground p-8 text-center">Loading…</div>}
      {!busy && data && grouped.length === 0 && (
        <Card className="p-8 rounded-sm text-center text-muted-foreground italic">No statutory deadlines in the next 180 days.</Card>
      )}
      {!busy && grouped.map(([mo, rows]) => (
        <Card key={mo} className="rounded-sm overflow-hidden">
          <div className="bg-muted/40 border-b p-3 text-[10px] font-mono uppercase tracking-widest text-accent">
            {new Date(mo + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </div>
          <table className="w-full text-sm">
            <tbody>
              {rows.map((d, i) => {
                const urgent = d.days_to_go <= 7;
                const warn = d.days_to_go <= 30;
                return (
                  <tr key={i} className={`border-b last:border-b-0 hover:bg-muted/20 ${urgent ? "bg-red-50/50" : warn ? "bg-amber-50/40" : ""}`} data-testid={`statutory-row-${d.form.replace(/[^a-z0-9]+/gi, "-")}`}>
                    <td className="p-3 w-24">
                      <div className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm inline-block border ${catColor(d.category)}`}>{d.category}</div>
                    </td>
                    <td className="p-3 font-mono font-semibold">{d.form}</td>
                    <td className="p-3 text-muted-foreground">{d.purpose}</td>
                    <td className="p-3 text-right tabular-nums font-mono">{d.due}</td>
                    <td className="p-3 text-right w-32">
                      <span className={`text-xs font-semibold ${urgent ? "text-red-700" : warn ? "text-amber-700" : "text-muted-foreground"}`}>
                        {d.days_to_go === 0 ? "Due today" : d.days_to_go === 1 ? "Tomorrow" : `${d.days_to_go} days`}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// E-Invoice / IRN Calendar (existing)
// -----------------------------------------------------------------------------
function EInvoicePanel() {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/einvoice/calendar", { params: { month } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load calendar"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [month]);

  const grid = useMemo(() => {
    if (!data) return { weeks: [], monthLabel: "" };
    const [y, mo] = month.split("-").map(Number);
    const firstDay = new Date(y, mo - 1, 1).getDay();
    const leading = (firstDay + 6) % 7;
    const cells = [...Array(leading).fill(null), ...data.days];
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return { weeks, monthLabel: new Date(y, mo - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) };
  }, [data, month]);

  const cellColor = (d) => {
    if (!d || d.total === 0) return "bg-muted/30";
    if (d.anomaly) return "bg-red-50 border-red-300";
    if (d.missing_irn > 0) return "bg-amber-50 border-amber-300";
    return "bg-emerald-50 border-emerald-200";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// E-Invoice IRN activity</div>
          <div className="text-2xl font-semibold mt-1">{grid.monthLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} className="rounded-sm" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => setMonth(todayMonth())} className="rounded-sm text-xs">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} className="rounded-sm" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {data?.totals && (
        <div className="grid grid-cols-5 gap-3">
          <div className="card-sharp p-3"><div className="text-[10px] uppercase font-mono text-muted-foreground">Invoices</div><div className="text-xl font-bold" data-testid="cal-total-invoices">{data.totals.invoices}</div></div>
          <div className="card-sharp p-3"><div className="text-[10px] uppercase font-mono text-emerald-700">With IRN</div><div className="text-xl font-bold text-emerald-700">{data.totals.with_irn}</div></div>
          <div className="card-sharp p-3"><div className="text-[10px] uppercase font-mono text-amber-700">Missing IRN</div><div className="text-xl font-bold text-amber-700">{data.totals.missing_irn}</div></div>
          <div className="card-sharp p-3"><div className="text-[10px] uppercase font-mono text-blue-700">With EWB</div><div className="text-xl font-bold text-blue-700">{data.totals.with_ewb}</div></div>
          <div className={`card-sharp p-3 ${data.totals.anomalies > 0 ? "border-red-300 bg-red-50" : ""}`}>
            <div className="text-[10px] uppercase font-mono text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Anomaly days</div>
            <div className="text-xl font-bold text-red-700" data-testid="cal-anomalies">{data.totals.anomalies}</div>
          </div>
        </div>
      )}

      <div className="flex gap-4 text-[11px] items-center text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-200"></span>All IRN issued</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-50 border border-amber-300"></span>Some missing IRN</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-red-50 border border-red-300"></span>Anomaly (majority missing / no EWB / mass cancel)</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-muted/30"></span>No invoices</span>
      </div>

      {busy && <div className="text-muted-foreground p-8 text-center">Loading…</div>}
      {!busy && data && (
        <div className="card-sharp overflow-hidden" data-testid="cal-grid">
          <div className="grid grid-cols-7 border-b bg-muted/20 text-[10px] font-mono uppercase text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (<div key={d} className="p-2 text-center">{d}</div>))}
          </div>
          {grid.weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
              {week.map((d, di) => (
                <button key={di} onClick={() => d?.total > 0 && setSelectedDay(d)}
                  className={`min-h-[110px] p-2 text-left border-r last:border-r-0 relative transition-colors ${cellColor(d)} ${d?.total > 0 ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
                  data-testid={d ? `cal-day-${d.date}` : undefined}>
                  {d && (<>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold">{d.date.slice(8)}</span>
                      {d.anomaly && <AlertTriangle className="w-3 h-3 text-red-600" title={d.anomaly_note} />}
                    </div>
                    {d.total > 0 && (
                      <div className="mt-1.5 space-y-0.5 text-[10px] font-mono">
                        <div className="flex justify-between"><span className="text-muted-foreground">Inv</span><span>{d.total}</span></div>
                        {d.with_irn > 0 && <div className="flex justify-between text-emerald-700"><span>IRN</span><span>{d.with_irn}</span></div>}
                        {d.missing_irn > 0 && <div className="flex justify-between text-amber-700"><span>Miss</span><span>{d.missing_irn}</span></div>}
                        {d.with_ewb > 0 && <div className="flex justify-between text-blue-700"><span>EWB</span><span>{d.with_ewb}</span></div>}
                        <div className="pt-0.5 border-t border-current opacity-40 text-[10px]">₹{fmtINR(d.revenue)}</div>
                      </div>
                    )}
                    {d.anomaly && (<div className="absolute bottom-1 left-2 right-2 text-[9px] text-red-700 font-medium leading-tight">{d.anomaly_note}</div>)}
                  </>)}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <SheetContent className="w-[560px] sm:max-w-lg overflow-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <span>{selectedDay?.date}</span>
              {selectedDay?.anomaly && <span className="text-red-600 text-xs bg-red-100 px-2 py-0.5 rounded-sm">⚠ {selectedDay.anomaly_note}</span>}
            </SheetTitle>
          </SheetHeader>
          {selectedDay && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 rounded-sm p-2"><div className="text-lg font-bold text-emerald-700">{selectedDay.with_irn}</div><div className="text-[10px] uppercase">With IRN</div></div>
                <div className="bg-amber-50 rounded-sm p-2"><div className="text-lg font-bold text-amber-700">{selectedDay.missing_irn}</div><div className="text-[10px] uppercase">Missing</div></div>
                <div className="bg-blue-50 rounded-sm p-2"><div className="text-lg font-bold text-blue-700">{selectedDay.with_ewb}</div><div className="text-[10px] uppercase">EWB</div></div>
              </div>
              <div className="border-t pt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Invoices on this day</div>
                <div className="space-y-1">
                  {selectedDay.invoices.map((iv, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b last:border-b-0" data-testid={`cal-inv-${iv.id}`}>
                      <div className="flex-1">
                        <div className="font-mono">{iv.invoice_number}</div>
                        <div className="text-[10px] text-muted-foreground flex gap-2">
                          {iv.irn ? <span className="text-emerald-700">✓ IRN</span> : <span className="text-amber-700">✗ No IRN</span>}
                          {iv.ewb_no && <span className="text-blue-700">✓ EWB</span>}
                          {iv.status === "cancelled" && <span className="text-red-600">CANCELLED</span>}
                        </div>
                      </div>
                      <div className="text-right font-mono">₹{fmtINR(iv.grand_total)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground bg-accent/5 rounded-sm p-2">
                <Sparkles className="w-3 h-3 text-accent" />
                Click any invoice number in your Sales screen to generate its IRN / EWB in one click.
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main component with tabs
// -----------------------------------------------------------------------------
export default function ComplianceCalendar() {
  return (
    <div className="p-6 space-y-4" data-testid="compliance-calendar-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Compliance calendar</div>
        <div className="text-2xl font-semibold mt-1">Every statutory deadline · One view</div>
        <div className="text-sm text-muted-foreground">MCA · Income Tax · TDS · Advance Tax + Live E-Invoice activity. Enable T-7 / T-1 email reminders to never miss a filing.</div>
      </div>

      <Tabs defaultValue="statutory" className="w-full">
        <TabsList className="grid grid-cols-2 max-w-md rounded-sm bg-muted/50 h-auto p-1">
          <TabsTrigger value="statutory" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="tab-statutory"><CalendarDays className="w-3.5 h-3.5" /> Statutory Deadlines</TabsTrigger>
          <TabsTrigger value="einvoice" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="tab-einvoice"><ReceiptText className="w-3.5 h-3.5" /> E-Invoice Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="statutory" className="mt-4"><StatutoryPanel /></TabsContent>
        <TabsContent value="einvoice" className="mt-4"><EInvoicePanel /></TabsContent>
      </Tabs>
    </div>
  );
}
